import { useEffect, useState } from 'react';

import { Box, Link } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';

import { userInfoAtom } from '~atoms';

import { useStyles } from './style';
import { Table, Label } from '../../../../components';
import { isObject, decimalAmount, getFinanceTableHeaders } from '../../../../shared/utility';
import * as actions from '../../../../store/actions';

interface HistoryProps {
	onFetchExpenses?(...args: unknown[]): unknown;
	isSuperAdmin?: boolean;
	expenses?: {
		data?: object;
		loading?: boolean;
		error?: object | string;
	};
}

const History = (props: HistoryProps) => {
	const { onFetchExpenses, expenses, isSuperAdmin } = props;

	const { t } = useTranslation();
	const classes = useStyles();
	const userInfo = useAtomValue(userInfoAtom);

	const [shouldDoInitialFetch, setShouldDoInitialFetch] = useState(true);
	const [showingInitialResults, setShowingInitialResults] = useState(true);

	const [pageNumber, setPageNumber] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const page = { number: pageNumber, size: pageSize };

	const filters = {};

	const { data: expensesData, loading: expensesLoading, error: expensesError } = expenses;
	const expensesReady = isObject(expensesData) && !expensesLoading && !expensesError;

	useEffect(() => {
		if (shouldDoInitialFetch) {
			if (!expensesLoading) {
				onFetchExpenses(page, filters);
			}
			if (showingInitialResults) {
				setShowingInitialResults(false);
			}
		}
	}, [pageNumber, pageSize]);

	const handlePageChange = (newPage) => {
		setPageNumber(newPage);
		setShouldDoInitialFetch(true);
	};

	const handlePageSizeChange = (newSize) => {
		setPageSize(newSize);
		setPageNumber(1);
		setShouldDoInitialFetch(true);
	};

	const statusColor = {
		open: 'default',
		paid: 'success',
	};

	const statusText = {
		open: t('ui.open'),
		paid: t('ui.label.paid'),
	};

	const tableHeader = getFinanceTableHeaders();

	const handlePathName = (start, title) => {
		const path = `/financeManagement/expenses/${start}/${title}/`;
		if (isSuperAdmin) {
			return `${path}organisations/expenses`;
		} else {
			return `${path}partner/expenses/${userInfo.organisation.id}/${userInfo.organisation.label}`;
		}
	};

	const tableBody =
		expensesReady && isObject(expensesData) ?
			expensesData.results.map((history) => [
				{
					content: (
						<Box className={classes.cell}>
							<Link
								color='inherit'
								component={RouterLink}
								to={handlePathName(history.start, history.title)}
							>
								{`${history.title}`}
							</Link>
						</Box>
					),
				},
				{ content: `${decimalAmount(history.amount.subtotal, history.amount)}` },
				{ content: `${decimalAmount(history.amount.discount, history.amount)}` },
				{
					content: `${decimalAmount(history.amount.subtotal - history.amount.discount, history.amount)}`,
				},
				{ content: `${decimalAmount(history.amount.priceVat, history.amount)}` },
				{ content: `${decimalAmount(history.amount.refund, history.amount)}` },
				{ content: `${decimalAmount(history.amount.totalPrice, history.amount)}` },
				{ content: `${decimalAmount(history.open, history.amount)}` },
				{
					content: (
						<Box className={classes.status}>
							<Label type={statusColor[history.status]}>{statusText[history.status]}</Label>
						</Box>
					),
				},
			])
		:	Array(5)
				.fill(Array(tableHeader.length).fill())
				.map((arr) => arr.map(() => ({ loading: true })));

	return (
		<Box pt={4.3}>
			<Table
				body={tableBody}
				cellStyle={classes.cellStyle}
				data={expensesData ? expensesData.results : []}
				handlePageChange={handlePageChange}
				header={tableHeader}
				loading={expensesLoading}
				page={pageNumber}
				rowsPerPage={pageSize}
				setRowsPerPage={handlePageSizeChange}
				title={t('views.tableResults.revenue.table.title')}
				total={expensesData ? expensesData.total : 0}
				withoutPages={true}
			/>
		</Box>
	);
};

const mapStateToProps = (state) => {
	return {
		expenses: state.paged.expenses,
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		onFetchExpenses: (page, filters) => dispatch(actions.fetchExpenses(page, filters)),
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(History);
